import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  LoadingButton,
} from "@altimateai/lego";
import { useEffect } from "react";
import { useCreateSSOGroupMapping, useUpdateSSOGroupMapping } from "./hooks";
import { toast } from "@altimateai/lego";
import type { SSOGroupMappingResponse } from "./types";
import { MATCH_TYPES } from "./types";
import { GroupIdentifierInput } from "./GroupIdentifierInput";

const formSchema = z.object({
  external_group_identifier: z.string().min(1, "Group identifier is required"),
  match_type: z.enum(["exact", "prefix", "contains", "suffix"]),
  target_id: z.number({ required_error: "Target is required" }).min(1, "Target is required"),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mappingType: "team" | "role";
  editMapping?: SSOGroupMappingResponse | null;
  teams: Array<{ id: number; name: string }>;
  roles: Array<{ id: number; role_title: string }>;
}

export const AddMappingDialog = ({
  open,
  onOpenChange,
  mappingType,
  editMapping,
  teams,
  roles,
}: Props) => {
  const isEditing = !!editMapping;
  const createMutation = useCreateSSOGroupMapping();
  const updateMutation = useUpdateSSOGroupMapping();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      external_group_identifier: "",
      match_type: "exact",
      target_id: 0,
    },
  });

  useEffect(() => {
    if (!open) return;
    if (editMapping) {
      form.reset({
        external_group_identifier: editMapping.external_group_identifier,
        match_type: editMapping.match_type as FormData["match_type"],
        target_id: editMapping.target_id,
      });
    } else {
      form.reset({
        external_group_identifier: "",
        match_type: "exact",
        target_id: 0,
      });
    }
  }, [editMapping, open, form]);

  const targets =
    mappingType === "team"
      ? teams.map(t => ({ id: t.id, label: t.name }))
      : roles.map(r => ({ id: r.id, label: r.role_title }));

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing && editMapping) {
        await updateMutation.mutateAsync({
          mappingId: editMapping.id,
          data: {
            external_group_identifier: data.external_group_identifier,
            match_type: data.match_type,
            target_id: data.target_id,
          },
        });
        toast({ title: "Mapping updated successfully" });
      } else {
        await createMutation.mutateAsync({
          ...data,
          mapping_type: mappingType,
        });
        toast({ title: "Mapping created successfully" });
      }
      onOpenChange(false);
      form.reset();
    } catch (error) {
      toast({
        variant: "destructive",
        title: isEditing ? "Failed to update mapping" : "Failed to create mapping",
        description: String(error),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="al-max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} {mappingType === "team" ? "Team" : "Role"} Mapping
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-4">
            <FormField
              control={form.control}
              name="external_group_identifier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>External Group Identifier</FormLabel>
                  <FormControl>
                    <GroupIdentifierInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="e.g., engineering-team"
                    />
                  </FormControl>
                  <FormDescription>
                    Type the group name from your identity provider. Suggestions appear from
                    uploaded SSO groups.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="match_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Match Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select match type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MATCH_TYPES.map(mt => (
                        <SelectItem key={mt.value} value={mt.value}>
                          {mt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="target_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{mappingType === "team" ? "Team" : "Role"}</FormLabel>
                  <Select
                    onValueChange={val => field.onChange(Number(val))}
                    value={field.value ? String(field.value) : ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${mappingType}`} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {targets.map(t => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <LoadingButton type="submit" loading={isSubmitting}>
                {isEditing ? "Update" : "Create"}
              </LoadingButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
